/**
 * MappingHistoryTab — Phase R10.4c (2026-07-05)
 *
 * Timeline of audit events for a single mapping. Opened from the
 * Saved tab's "History" link.
 *
 * Props:
 *   mapping         — the mapping row (used for header context)
 *   onBack()        — navigates back to Saved tab
 */
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, FilePlus2, Pencil, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { SectionCard, QueryError } from '@/components/ui_primitives/Misc';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('id-ID', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const ACTION_META = {
  'outlet_mapping.created': { label: 'Created',    Icon: FilePlus2, cls: 'text-emerald-300' },
  'outlet_mapping.updated': { label: 'Updated',    Icon: Pencil,    cls: 'text-gold' },
};

export default function MappingHistoryTab({ mapping, onBack }) {
  const token = useAuthStore((s) => s.token);
  const historyQ = useQuery({
    queryKey: ['owner-outlet-mapping-history', mapping?.id],
    queryFn:  () => api.ownerGetOutletMappingHistory(token, mapping.id),
    enabled:  !!token && !!mapping?.id,
    staleTime: 10_000,
  });

  if (!mapping) return null;

  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onBack}
          data-testid="outlet-mapping-history-back"
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to Saved Mappings
        </button>
        <div className="text-xs text-white/50">
          History ·
          <span className="ml-1 font-semibold text-white">
            {mapping.original_detected_outlet}
          </span>
        </div>
      </div>

      {historyQ.isLoading ? (
        <div className="py-10 flex items-center justify-center gap-2 text-white/55 text-sm">
          <Loader2 size={16} className="animate-spin text-gold" />
          Loading history…
        </div>
      ) : historyQ.isError ? (
        <QueryError error={historyQ.error} onRetry={historyQ.refetch} />
      ) : (historyQ.data?.items ?? []).length === 0 ? (
        <div className="py-10 text-center text-sm text-white/50 border border-dashed border-white/10 rounded-2xl">
          No audit entries yet.
        </div>
      ) : (
        <ol className="space-y-2" data-testid="outlet-mapping-history-rows">
          {(historyQ.data.items ?? []).map((entry) => {
            const meta = ACTION_META[entry.action] || {
              label: entry.action.split('.').pop() ?? entry.action,
              Icon:  Pencil,
              cls:   'text-white/60',
            };
            const metadata = entry.metadata || {};
            const before   = metadata.before;
            const after    = metadata.after;
            return (
              <li
                key={entry.id}
                className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                data-testid={`outlet-mapping-history-row-${entry.action.replace(/\./g, '-')}`}
              >
                <div className="flex items-start gap-3">
                  <meta.Icon size={14} className={`${meta.cls} mt-0.5 shrink-0`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm text-white font-semibold">
                        {meta.label}
                      </div>
                      <div className="text-[11px] text-white/40">
                        {fmtDate(entry.created_at)}
                      </div>
                    </div>
                    <div className="text-xs text-white/50 font-mono mt-0.5">
                      actor: {entry.actor_user_id?.slice(0, 8) ?? 'unknown'}…
                    </div>
                    {before && after ? (
                      <div className="text-xs text-white/70 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="rounded-md bg-white/5 px-2 py-0.5 font-mono">
                          {JSON.stringify(before)}
                        </span>
                        <ArrowRight size={10} className="text-white/40" />
                        <span className="rounded-md bg-white/5 px-2 py-0.5 font-mono">
                          {JSON.stringify(after)}
                        </span>
                      </div>
                    ) : metadata.mapped_branch_id ? (
                      <div className="text-xs text-white/70 mt-1">
                        branch:{' '}
                        <span className="font-mono text-[11px]">
                          {metadata.mapped_branch_name || metadata.mapped_branch_id}
                        </span>
                        {typeof metadata.applied_to_claims_count === 'number' ? (
                          <span className="ml-2 text-white/50">
                            · applied to {metadata.applied_to_claims_count}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}
